import {inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {JwtHelperService} from '@auth0/angular-jwt';
import {tap} from 'rxjs/operators';
import {Router} from '@angular/router';
import {LoginRequest} from '../models/login-request';
import {Store} from "@ngxs/store";
import {SessionState} from 'src/app/shared/state/session-state';
import {ToastService} from '@ratespecial/core';

/**
 * NOTE: This service is not responsible for maintaining the JWT auth token, nor should it hold events to let
 * the app know of changes in auth state.  That is handled my the NGXS state engine.
 */
@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private notificationService = inject(ToastService);
  private store = inject(Store);


  /**
   * JwtHelper Vendor Library
   */
  private jwtHelper: JwtHelperService;

  /**
   * ID for Auto Logout Timer
   */
  private autoLogoutTimerId;


  constructor() {
    this.jwtHelper = new JwtHelperService();
  }

  /**
   * Return true if JWT has expired
   */
  isTokenExpired(): boolean {
    const token = this.store.selectSnapshot(SessionState.token);
    return this.jwtHelper.isTokenExpired(token);
  }


  /**
   * Return true is user is currently authenticated
   */
  isAuthenticated(): boolean {
    const token = this.store.selectSnapshot(SessionState.token);

    // Warning: using PHPStorms "simplify this" will cause this to return null, and things break.
    return (token && token.length > 0 && this.isTokenExpired() === false);
  }


  /**
   * Logout - Clear auth tokens, save last route and kick user back to login page
   */
  logout(showExpirationError = false): void {

    this.http.post('/api/auth/logout', {}).subscribe();

    // kept getting: Navigation triggered outside Angular zone, did you forget to call 'ngZone.run()'?
    // could be something to do with ngxs.  i moved this functionality to AppComponent
    // this.router.navigateByUrl('/auth/logout');

    if (showExpirationError) {
      this.notificationService.error('Your session has expired.  Please log in.');
    }

    clearTimeout(this.autoLogoutTimerId);
  }


  localLogin(loginRequest: LoginRequest) {
    return this.http
      .post('/api/auth/login', loginRequest)
      .pipe(
        tap(() => this.setExpirationTimer(5))
      );
  }

  logoutServer() {
    return this.http
      .post('/api/auth/logout', {})
      .pipe(
        tap(() => {
          this.router.navigateByUrl('/auth/logout');
          clearTimeout(this.autoLogoutTimerId);
        })
      );
  }


  /**
   * Starts time to auto logout user based on JWT expiration time
   */
  private setExpirationTimer(offsetSeconds = 0): void {

    let timeout = 3600000;     // 3,600,000 = 1 hour in milliseconds
    const token = this.store.selectSnapshot(SessionState.token);

    // If we have a formal token expiration time from server, use that instead.
    const expirationDate = this.jwtHelper.getTokenExpirationDate(token);
    if (expirationDate !== null) {
      timeout = (expirationDate.valueOf() - (offsetSeconds * 1000)) - new Date().valueOf();
    }

    // set the logout timer
    this.autoLogoutTimerId = setTimeout(() => {
      this.logout();
    }, timeout);
  }

}
